/**
 * Shared collision-geometry — the single source of truth for WHICH role-
 * resolution outcomes a race-vs-race collision offers.
 *
 * Imported by BOTH the client (RoleResolutionSheet + add/window pre-checks,
 * via `@shared/modules/journeys/collisionGeometry`) and the server (the
 * `createJourney` overlap gate, `window_covers_events`, reclassify-promote),
 * so the two sides can never drift on which outcomes are geometrically valid.
 * See docs/events-journeys-role-conflict-spec.md §7 / §7.1.
 *
 * Deliberately dependency-light and PURE: no I/O, no `Date.now`, no Prisma.
 * It imports only the pure YMD helper `compareYmd` from `src/lib/customerTime.ts`
 * (TZ-independent string math) so the client bundles it unchanged via `@shared`.
 * Callers own date resolution — the module takes only resolved `[start, race)`
 * windows.
 *
 * The §7.1 direction rule lives here: the EARLIER race always nests into the
 * LATER race's build (the later race is the anchor). Never both nest outcomes.
 */

import { compareYmd } from "../../lib/customerTime.js";

/** An event participating in a collision. `hasJourney` = it already anchors a build. */
export type CollisionEvent = { id: string; ymd: string; hasJourney: boolean };

/**
 * The three archetypal outcomes (spec §5 / §7):
 *   - `nest_new`      → mark the NEWLY-added event a milestone, do NOT create its
 *                       journey (used when the new race is the EARLIER one).
 *   - `nest_existing` → today's `demote`: archive the EXISTING journey, its event
 *                       → milestone (used when the existing race is the EARLIER one).
 *   - `chain`         → two separate builds, back-to-back. ALWAYS offered on a
 *                       real overlap, in BOTH directions: new-later chains the
 *                       new build after the existing race; new-earlier resumes
 *                       the existing build after the new race (front-truncated).
 * Stable render/wire order: nest_*, then chain.
 * (The former `split` "hand over" outcome was removed from the pair-overlap path.)
 */
export type CollisionOutcome = "nest_new" | "nest_existing" | "chain";

/** Each event's resolved build window `[start, race)`; race day excluded from the window. */
export type CollisionWindows = {
  newStartYmd: string;
  newRaceYmd: string;
  exStartYmd: string;
  exRaceYmd: string;
};

export type CollisionResult = {
  collides: boolean;
  /** The LATER race id — the natural anchor. `null` when there is no collision (incl. same-day). */
  anchorId: string | null;
  /** The EARLIER race id (nests into the anchor's build). `null` when no collision. */
  earlierId: string | null;
  /** Only the geometrically valid outcomes, stable order: nest_*, chain. */
  outcomes: CollisionOutcome[];
};

const NO_COLLISION: CollisionResult = {
  collides: false,
  anchorId: null,
  earlierId: null,
  outcomes: []
};

/**
 * Decide which outcomes a collision between `newEvent` and `existingEvent`
 * offers, given their resolved windows `w`.
 *
 * Rules (mirror service.ts:312 / :485-491 / §7.1):
 *   - Overlap iff `newStart <= exRace && exStart <= newRace` (inclusive-interval);
 *     no overlap → no collision, no outcomes.
 *   - Same race day (`newRaceYmd === exRaceYmd`) → NOT a collision (SF2: the
 *     add path treats this as archive-and-replace, not a role decision).
 *   - Anchor = the later race. `chain` is ALWAYS offered on a real overlap
 *     (both directions — the product model: "Keep them separate" is always a
 *     choice for an overlapping pair). The executor is direction-aware (see
 *     service.ts `applyOverlapResolution`): new-later moves the new build after
 *     the existing race; new-earlier front-truncates the existing build to
 *     resume after the new race.
 *   - Nest direction (SF1): existing earlier → offer `nest_existing`; new earlier
 *     → offer `nest_new`. Never both. Nest is ALWAYS offered → outcomes is never
 *     empty on a real collision.
 *   - Net: a real overlap yields exactly [nest_existing | nest_new, "chain"].
 *
 * NOTE (SF8): rolling journeys have no race day and never collide — callers must
 * exclude them before calling (this module has no concept of "rolling").
 */
export function evaluateCollision(
  newEvent: CollisionEvent,
  existingEvent: CollisionEvent,
  w: CollisionWindows
): CollisionResult {
  // Inclusive-interval overlap — same predicate as the server gate (service.ts:312).
  const overlaps =
    compareYmd(w.newStartYmd, w.exRaceYmd) <= 0 &&
    compareYmd(w.exStartYmd, w.newRaceYmd) <= 0;
  if (!overlaps) return NO_COLLISION;

  // Same race day → archive-and-replace, not a collision (SF2).
  if (compareYmd(w.newRaceYmd, w.exRaceYmd) === 0) return NO_COLLISION;

  // Anchor is the later race; the earlier race nests into it (§7.1 invariant).
  const newIsLater = compareYmd(w.newRaceYmd, w.exRaceYmd) > 0;
  const anchorId = newIsLater ? newEvent.id : existingEvent.id;
  const earlierId = newIsLater ? existingEvent.id : newEvent.id;

  const outcomes: CollisionOutcome[] = [];
  // Nest direction (SF1): existing earlier → nest_existing; new earlier → nest_new.
  if (compareYmd(w.exRaceYmd, w.newRaceYmd) < 0) {
    outcomes.push("nest_existing");
  } else {
    // newRaceYmd < exRaceYmd (equality already handled above).
    outcomes.push("nest_new");
  }
  // chain: "keep them separate" is ALWAYS a choice for a real overlap, both
  // directions — the executor front-truncates whichever plan owns the losing
  // span (new-later: new build starts after ex race; new-earlier: existing
  // build resumes after the new race). See service.ts `applyOverlapResolution`.
  outcomes.push("chain");

  return { collides: true, anchorId, earlierId, outcomes };
}
